import { FormInstance, Tag } from "antd";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  QueryFieldMapping,
} from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
  createStaticOptionsRequest,
} from "../../hooks/useSearchRequest";
import {
  getWellbeingMaterials,
  getPlatformUsers,
  getCompanies,
} from "../../repository/data.repository";
import { MaterialEngagementStatus } from "../../repository/data.default";

// Per-status tag label + color. The records grid only ever sees InProgress/Completed rows; the computed
// "Not Started" state belongs to the Coverage view (it has no stored row to list/filter here).
const STATUS_META: Record<string, { readonly label: string; readonly color: string }> = {
  [MaterialEngagementStatus.NotStarted]: { label: "Not Started", color: "default" },
  [MaterialEngagementStatus.InProgress]: { label: "In Progress", color: "blue" },
  [MaterialEngagementStatus.Completed]: { label: "Completed", color: "green" },
};

const statusRequest = createStaticOptionsRequest([
  { display: "In Progress", value: MaterialEngagementStatus.InProgress },
  { display: "Completed", value: MaterialEngagementStatus.Completed },
]);

export const wellbeingMaterialEngagementViewColumns: DataColumn[] = [
  {
    title: "Material",
    key: "materialName",
    width: 224,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "User",
    key: "userUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
  },
  {
    title: "Started",
    key: "startedAt",
    width: 168,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  {
    title: "Completed",
    key: "completedAt",
    width: 168,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  {
    title: "Status",
    key: "status",
    width: 130,
    sort: true,
    transform: (value: any) => {
      const meta = STATUS_META[value] ?? { label: value, color: "default" };
      return <Tag color={meta.color}>{meta.label}</Tag>;
    },
  },
];

// Filterable by material, company, user, and completion status — every one backed by the engagement DTO.
// Only the user's own field is a query condition; company above it is also a real filter AND scopes the user list.
const wellbeingMaterialEngagementQueryMappings: QueryFieldMapping[] = [
  {
    formField: "material",
    queryField: "materialUid",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
  {
    formField: "company",
    queryField: "companyUid",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
  {
    formField: "user",
    queryField: "userUid",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
  {
    formField: "status",
    queryField: "status",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
];

export const mapWellbeingMaterialEngagementViewQuery = (
  values: any,
): SearchQuery =>
  buildSearchQuery(values, wellbeingMaterialEngagementQueryMappings);

// Factory so the User filter can scope to the chosen Company (read live off the grid's own form).
export const wellbeingMaterialEngagementSearchSchema = (
  form: FormInstance<any>,
): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "material",
          label: "Material",
          request: createSearchRequest(getWellbeingMaterials, "name"),
        },
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "user",
          label: "User",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "status",
          label: "Status",
          request: statusRequest,
        },
      ],
    },
  ],
});
